import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Mail, ArrowLeft, Loader2, CheckCircle } from "lucide-react";

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess(true);
      toast.success("If this email is registered, a reset link has been sent.");
    } catch (error) {
      // Still show success to prevent email enumeration
      setSuccess(true);
      toast.success("If this email is registered, a reset link has been sent.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-slate-900 relative"
      style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1705417272217-490f4511abeb?w=1920&q=80')",
        backgroundSize: "cover",
        backgroundPosition: "center"
      }}
    >
      <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" />
      
      <Card className="w-full max-w-md mx-4 relative z-10 border-slate-200 shadow-2xl" data-testid="forgot-password-card">
        <CardHeader className="space-y-1 text-center pb-2">
          <div className="w-14 h-14 bg-orange-600 rounded-md mx-auto mb-4 flex items-center justify-center">
            {success ? (
              <CheckCircle className="w-7 h-7 text-white" />
            ) : (
              <Mail className="w-7 h-7 text-white" />
            )}
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight font-heading">
            {success ? "Check Your Email" : "Reset Password"}
          </CardTitle>
          <CardDescription className="text-slate-500">
            {success 
              ? "If an account exists for this email, you'll receive a password reset link shortly."
              : "Enter your email and we'll send you a reset link"
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="space-y-4">
              <p className="text-center text-slate-600 text-sm">
                Didn't receive the email? Check your spam folder or{" "}
                <button 
                  onClick={() => setSuccess(false)} 
                  className="text-orange-600 hover:text-orange-700"
                >
                  try again
                </button>
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full h-11">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Sign In
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11"
                  data-testid="email-input"
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium"
                disabled={loading}
                data-testid="reset-button"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </span>
                ) : (
                  "Send Reset Link"
                )}
              </Button>
              
              <Link to="/login" className="block">
                <Button variant="ghost" className="w-full h-11">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Sign In
                </Button>
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
